import { EventServiceFacade } from '../../../JWeb';
import {
  DataCollectionEventNames,
  AssetUnitType
} from '../../../JWeb/JWebEnums';
import {
  CDMEventData,
  DataValveCDMEventData,
  Publisher
} from '../../../JWeb/types';
import {
  DataValveCDMEvent,
  GenericPublisher,
  PublishCdmEventsOptions,
  AnalyticsServiceInputType
} from '../../types';
import { hasErrorInEventServicePublisher } from './dependencies/utils';
import VisitUuidHandler from './dependencies/visitUuidHandler';
import { SetServiceDependencies } from '../../../../infra/startup/types';
import { internalLogger } from '../../../../interface/v1/clientOsLogger';
import { DataCollectionRecorderWeb } from './dependencies/DataCollectionRecorder';
import { OPT_IN_GROUP_VALUE } from '../../analyticsServiceConstants';
import BaseAnalyticsService from '../shared/BaseAnalyticsService';

// TODO: isolate this method.
async function _publishWeb(
  publisherWeb: Publisher,
  eventName: string,
  eventData: any
): Promise<void> {
  // validation only used in Web
  if ((window as any)._O?.ifOptInGroups(OPT_IN_GROUP_VALUE)) {
    await publisherWeb.publish(eventName, eventData);
  } else {
    internalLogger?.warn?.(
      'Analytics: Event(s) not sent. No AnalyticsOptInGroup.'
    );
  }
}

export default class WebAnalyticsService extends BaseAnalyticsService {
  private _dataCollectionRecorder = undefined; /// Only for Web
  private _visitUuidHandler;

  constructor(analyticsProps: AnalyticsServiceInputType) {
    super(analyticsProps);

    this._visitUuidHandler = new VisitUuidHandler(analyticsProps.userActivity);
    this._dataCollectionRecorder = new DataCollectionRecorderWeb(
      analyticsProps
    );
  }

  public setDependencies(setServiceDeps: SetServiceDependencies): void {
    super.setDependencies(setServiceDeps);

    const { services } = setServiceDeps;

    const authProvider = services.authProviderService;
    this._dataCollectionRecorder.setInterfaceDependencies({ authProvider });

    this._visitUuidHandler.setDependencies({ services });
  }

  public async init(): Promise<void> {
    // Get Publisher
    this._publisher = await this._getPublisher();

    // Checking if all JWEB dependences are ready.
    // TODO: I can't lock this call, because the commons uses await in this init().
    this.isEnabledWithAllPlugins();

    // Initialize Sub-Components
    this._visitUuidHandler.init();
    await this._dataCollectionRecorder.init();

    // Subscribe Events
    this._subscribeEvents();
  }

  private async _getPublisher(): Promise<GenericPublisher> {
    try {
      // Publisher for WEB
      const publisherId = `${window.location.hostname}.publisher`;

      const publisherWeb = (await EventServiceFacade.createPublisher(
        publisherId
      )) as Publisher;
      return {
        publish: (eventName, eventData) =>
          _publishWeb(publisherWeb, eventName, eventData)
      } as GenericPublisher;
    } catch (e) {
      internalLogger?.error?.('Analytics: getPublisher Error: ', e);
      this._isEnabled = false;
    }
  }

  public async publishCdmEvents(
    events: DataValveCDMEvent[],
    options?: PublishCdmEventsOptions
  ): Promise<void> {
    try {
      if (!this.isEnabled()) return;

      await this._visitUuidHandler.isVisitUUIDReady;

      if (hasErrorInEventServicePublisher(this._publisher)) return;

      internalLogger?.debug?.('Analytics: Publisher was found.');

      await this._writeAnalyticsDataToValueStore(options);
      await this._dataCollectionRecorder.updateDataCollectionService();

      const eventName = DataCollectionEventNames.cdmEvent;
      const eventData: DataValveCDMEventData = {
        events: events as unknown as [CDMEventData],
        trackingIdentifier: options?.metadata?.trackingIdentifier,
        valveControllerMetadata: {
          assetUnit: AssetUnitType.desktop
        }
      };

      // Publishes event
      internalLogger?.debug?.(
        'Analytics: publishCdmEvents is calling Publish method. '
      );
      await this._publisher.publish(eventName, eventData);
    } catch (e) {
      internalLogger?.error?.('Analytics: publishCdmEvents Internal Error', e);
    }
  }
}
